import { useEffect, useState } from 'react';
import { getIdentityLinks, type IdentityLink } from './api';
import { TopBar } from './components/TopBar';
import { LoadingState } from './components/states/LoadingState';
import { ErrorState } from './components/states/ErrorState';

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ok'; links: IdentityLink[] }
  | { kind: 'error'; message: string };

const COLUMNS = ['slackAlias', 'slackUserId', 'googleEmail', 'linkedAt', 'revoked'] as const;

/**
 * AOZONE-85: admin-only "Identity Links" observability view (reached via
 * `?view=identity-links`). Lists the current Slack↔Google bindings so the
 * product owner can confirm their own `/mbr link` bind appears. Read-only —
 * revoke is intentionally out of scope for now.
 */
export function IdentityLinksView() {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    getIdentityLinks()
      .then((links) => {
        if (!cancelled) setState({ kind: 'ok', links });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Session expired / not signed in — bounce through login like the
        // entries view rather than showing a raw error page.
        if (err instanceof Error && (err as Error & { status?: number }).status === 401) {
          window.location.assign('/api/auth/login');
          return;
        }
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : String(err),
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <TopBar userEmail={null} />
      <main className="mx-auto max-w-[1280px] px-6 py-8">
        <h1 className="mb-6 text-xl font-semibold tracking-tight">Identity Links</h1>
        {state.kind === 'loading' && <LoadingState />}
        {state.kind === 'error' && <ErrorState message={state.message} />}
        {state.kind === 'ok' && state.links.length === 0 && (
          <p className="py-16 text-center text-sm text-slate-500">No identity links yet.</p>
        )}
        {state.kind === 'ok' && state.links.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col} className="px-4 py-3 font-medium">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {state.links.map((link) => (
                  <tr
                    key={link.slackUserId}
                    className="border-b border-slate-100 last:border-0"
                  >
                    <td className="px-4 py-3">{link.slackAlias}</td>
                    <td className="px-4 py-3 font-mono text-xs">{link.slackUserId}</td>
                    <td className="px-4 py-3">{link.googleEmail}</td>
                    <td className="px-4 py-3">{link.linkedAt}</td>
                    <td className="px-4 py-3">{String(link.revoked)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
